import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, Plus, X, Download, Share } from 'lucide-react';

export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    let timerId = null;
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedAt < sevenDays) return;
    }

    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;

    const ua = window.navigator.userAgent;
    const isiOS = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
    const isSafari = /safari/i.test(ua) && !/chrome|crios|fxios/i.test(ua);
    
    if (isiOS && isSafari) {
      setIsIOS(true);
      setShowBanner(true);
      return;
    }

    const tryConsumeGlobalPrompt = () => {
      if (window.__pwaInstallPrompt) {
        setDeferredPrompt(window.__pwaInstallPrompt);
        setShowBanner(true);
        return true;
      }
      return false;
    };

    let attempt = 0;
    const check = () => {
      if (tryConsumeGlobalPrompt()) {
        if (timerId) clearTimeout(timerId);
        return;
      }
      const delay = Math.min(2000 + attempt * 2000, 10000); // Shorter for snappy PWA UX
      timerId = setTimeout(check, delay);
      attempt++;
    };

    if (!tryConsumeGlobalPrompt()) {
      timerId = setTimeout(check, 2000);
    }

    const handler = (e) => {
      e.preventDefault();
      if (timerId) clearTimeout(timerId);
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      if (timerId) clearTimeout(timerId);
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowBanner(false);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      console.log('✅ PWA installed');
    }
    setDeferredPrompt(null);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDeferredPrompt(null);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div 
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="install-pwa fixed bottom-0 left-0 right-0 z-[9999] p-4 flex justify-center pb-8"
        >
          <div className="w-full max-w-[480px] bg-[var(--color-bg-canvas-subtle)] border border-[color:rgba(var(--color-text-primary-rgb),0.08)] shadow-[0_-20px_50px_rgba(1, 4, 9,0.5)] rounded-[40px] p-6 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 blur-[60px] rounded-full group-hover:bg-primary/20 transition-all pointer-events-none" />
            
            <div className="flex items-center gap-5 relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-[color:rgba(var(--color-text-primary-rgb),0.02)] border border-[color:rgba(var(--color-text-primary-rgb),0.08)] p-1.5 flex items-center justify-center shrink-0 shadow-lg">
                <img src="/assets/mi/mi-icon-192.png" alt="Mi" className="w-full h-full rounded-xl object-contain" />
              </div>
              
              <div className="flex-1 space-y-1">
                <h3 className="text-sm font-extrabold text-[var(--color-text-primary)] uppercase tracking-tighter italic">Instalar Mi</h3>
                {isIOS ? (
                  <p className="text-[10px] text-muted font-bold leading-relaxed italic">
                    No Safari, toque <Share size={12} className="inline mx-1 text-primary" /> e selecione <br/>
                    <span className="text-[var(--color-text-primary)]">"Adicionar à Tela de Início"</span>
                  </p>
                ) : (
                  <p className="text-[10px] text-muted font-bold leading-relaxed italic">
                    Transforme os treinos em um App nativo para acesso instantâneo.
                  </p>
                )}
              </div>

              <button onClick={handleDismiss} className="p-2 text-muted/40 hover:text-[var(--color-text-primary)] transition-all">
                <X size={20} />
              </button>
            </div>

            <div className="flex gap-3 mt-6 relative z-10">
              <button 
                onClick={handleInstall} 
                className="flex-1 h-12 gph-btn-primary flex items-center justify-center gap-2 shadow-lg"
              >
                <Download size={16} />
                <span className="font-extrabold text-[10px] tracking-widest uppercase">{isIOS ? 'ENTENDI' : 'INSTALAR'}</span>
              </button>
              <button 
                onClick={handleDismiss} 
                className="flex-1 h-12 gph-btn-secondary font-extrabold text-[10px] tracking-widest uppercase"
              >
                AGORA NÁO
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}



